// ABOUTME: Email verification gate page shown to unverified users.
// ABOUTME: Provides resend verification email and re-authenticate actions.

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, RefreshCw, ArrowRight, CheckCircle } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/Button';

export function VerifyEmailPage() {
  const { user, logout } = useAuth();
  const { loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [checkingVerification, setCheckingVerification] = useState(false);

  const handleResendEmail = async () => {
    setResendState('sending');
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '/v1';
      const token = await getAccessTokenSilently();
      const response = await fetch(`${apiUrl}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        setResendState('sent');
      } else {
        setResendState('error');
      }
    } catch {
      setResendState('error');
    }
  };

  const handleContinue = async () => {
    setCheckingVerification(true);
    try {
      // Re-authenticate to get fresh token with updated email_verified claim
      await loginWithRedirect({
        appState: { returnTo: '/' },
      });
    } catch {
      setCheckingVerification(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-900 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-surface-0 dark:bg-surface-800 rounded-sm p-12 shadow-card border border-surface-200 dark:border-surface-700 max-w-md w-full"
      >
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-sm bg-primary-light dark:bg-primary/20 flex items-center justify-center">
            <Mail className="w-8 h-8 text-primary" />
          </div>
        </div>

        <h1 className="text-2xl font-semibold text-surface-800 dark:text-surface-100 mb-2 text-center">
          Verify your email
        </h1>
        <p className="text-surface-600 dark:text-surface-400 text-center mb-2">
          We sent a verification link to
        </p>
        <p className="text-surface-800 dark:text-surface-100 font-medium text-center mb-8">
          {user?.email}
        </p>

        <p className="text-surface-500 dark:text-surface-400 text-sm text-center mb-8">
          Click the link in the email to verify your account, then come back here and click continue.
        </p>

        <div className="space-y-3">
          <Button
            onClick={handleContinue}
            className="w-full"
            size="lg"
            disabled={checkingVerification}
            rightIcon={<ArrowRight className="w-5 h-5" />}
          >
            {checkingVerification ? 'Redirecting...' : "I've verified my email"}
          </Button>

          <Button
            onClick={handleResendEmail}
            variant="secondary"
            className="w-full"
            size="lg"
            disabled={resendState === 'sending' || resendState === 'sent'}
            leftIcon={
              resendState === 'sent'
                ? <CheckCircle className="w-5 h-5 text-success" />
                : <RefreshCw className={`w-5 h-5 ${resendState === 'sending' ? 'animate-spin' : ''}`} />
            }
          >
            {resendState === 'sent'
              ? 'Verification email sent!'
              : resendState === 'sending'
                ? 'Sending...'
                : 'Resend verification email'}
          </Button>
        </div>

        {resendState === 'error' && (
          <p className="text-error text-sm text-center mt-4">
            Failed to resend verification email. Please try again.
          </p>
        )}

        <div className="mt-8 pt-6 border-t border-surface-200 dark:border-surface-700">
          <button
            onClick={() => logout()}
            className="text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 text-sm w-full text-center transition-colors"
          >
            Sign out and use a different account
          </button>
        </div>
      </motion.div>
    </div>
  );
}
